import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import * as rxjs from 'rxjs'

import type { Feature } from '../../feature'
import * as models from '../../models'
import { getEdgeDataDefinition, getNodeDataDefinition } from '../elements'
import type { Manager } from '../manager'
import { refreshNodeClasses } from '../setup'

export const undoRedoInCytoscape = store.effects.create(
  (feature: Feature, manager: Manager) =>
    rxjs
      .merge(feature.actions.undo.observe(), feature.actions.redo.observe())
      .pipe(
        rxjsUtils.latestFrom(({ op, data: { chartId: orgChartId } }) => ({
          change: feature.selectors
            .getChart({ chartId: orgChartId })
            .asObservable.pipe(
              rxjs.map(orgChart => {
                if (op.name === 'undo') {
                  // We have to undo the current change from the history (since
                  // the undo count has already been updated)
                  const originalChange =
                    orgChart?.history[
                      orgChart.history.length - orgChart.undoCount
                    ]
                  return originalChange && models.Change.inverse(originalChange)
                } else {
                  // We have to redo the previous change from the history (since
                  // the undo count has already been updated)
                  return orgChart?.history[
                    orgChart.history.length - orgChart.undoCount - 1
                  ]
                }
              }),
            ),
          orgChartId: rxjs.of(orgChartId),
        })),
        rxjs.map(({ change, orgChartId }) => {
          const cy = manager.getInstance({ chartId: orgChartId })
          if (!change || !cy) {
            return
          }
          /* -------------------------------- Deletions ------------------------------- */
          // We delete edges before nodes because we can't have edges that don't
          // have corresponding nodes
          if (change.edges.deleted.length) {
            cy.edges()
              .filter(edge =>
                change.edges.deleted.some(
                  deletedEdge =>
                    deletedEdge.parentId === edge.source().id() &&
                    deletedEdge.childId === edge.target().id(),
                ),
              )
              .remove()
          }
          if (change.nodes.deleted.length) {
            cy.nodes()
              .filter(node =>
                change.nodes.deleted.some(
                  deletedNode =>
                    ents.core.display.tree.Node.Id.get(deletedNode) ===
                    node.id(),
                ),
              )
              .remove()
          }
          /* -------------------------------- Additions ------------------------------- */
          for (const node of change.nodes.added) {
            cy.add({ data: getNodeDataDefinition(node) })
          }
          for (const node of change.nodes.edited) {
            cy.getElementById(
              ents.core.display.tree.Node.Id.get(node.from),
            ).data(getNodeDataDefinition(node.to))
          }
          // We add edges after nodes because we can't have edges that don't have
          // corresponding nodes
          for (const edge of change.edges.added) {
            cy.add({ data: getEdgeDataDefinition(edge) })
          }

          refreshNodeClasses(cy, {
            chartId: orgChartId,
            feature,
          })

          manager.resetLayout({ chartId: orgChartId })
        }),
        rxjs.ignoreElements(),
      ),
)
